// Trapped piece. After the line's first move an enemy queen, rook or minor
// piece is attacked, cannot stay (it is not safe where it stands) and has
// nowhere to go: every legal move of that piece, by the rules, lands on a
// square where it is not safe either, unless the move takes something worth
// as much as the piece itself. The most valuable such piece names the
// variation: the queen, a rook or a minor piece.
import { uciToInput } from '@chess/api-chess';
import type { Motif } from '@chess/api-mistakes';
import type { TacticLine } from './line.ts';
import { Board, pieceClass, safe, VALUE, type Placed } from './rays.ts';

/** Whether the piece on `square` has a safe move in a position where its side is to move. */
function hasEscape(line: TacticLine, fen: string, board: Board, piece: Placed): boolean {
  return line.chess.legalMoves(fen, piece.square).some((move) => {
    const taken = board.get(move.to);
    if (taken && VALUE[taken.kind] >= VALUE[piece.kind]) return true;
    return safe(board.play(move.uci), move.to);
  });
}

export function trappedPiece(line: TacticLine): Motif | undefined {
  const first = line.pv[0];
  if (!first) return undefined;
  const played = line.chess.play(line.fen, uciToInput(first));
  if (!played) return undefined;
  const board = Board.fromFen(played.fen);
  const caught = board
    .pieces()
    .filter((p) => p.colour === line.victim && pieceClass(p.kind) !== undefined && p.kind !== 'pawn')
    .filter((p) => board.attackers(p.square, line.side).length > 0 && !safe(board, p.square))
    .filter((p) => !hasEscape(line, played.fen, board, p))
    .sort((a, b) => VALUE[b.kind] - VALUE[a.kind])[0];
  if (!caught) return undefined;
  const variation = pieceClass(caught.kind);
  return variation && variation !== 'pawn' ? { family: 'trappedPiece', variation } : undefined;
}
